import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';

import { ConfigResponse } from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { Button } from '../../components/core/Button/Button';
import { useGetConfigsQuery, useRegisterCtpMutation } from '../../store/modules/merchant/api';
import { useMerchant } from '../../contexts/MerchantContext';
import { useIsSkipifyAdmin } from '../../hooks';
import { configurationPaths } from '../../util/constants';
import { toast } from '../../util/toast';
import { Alert } from '../Paylinks/components/Alert';

type ClickToPayConfig = {
  srcDpaId: string;
  dpaName: string;
  dynamicDataType: string;
};

export const ClickToPay = () => {
  const { merchantId, isLoading: merchantInfoLoading } = useMerchant();
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const [registerCtp, { isLoading: isRegistering }] = useRegisterCtpMutation();

  // Check admin permissions first
  const isSkipifyAdmin = useIsSkipifyAdmin();
  if (!isSkipifyAdmin) {
    return <Navigate to={configurationPaths.SETTINGS} replace />;
  }

  const ctpConfig = configs?.find((c) => c.name === 'CLICK_TO_PAY') as
    ConfigResponse<ClickToPayConfig> | undefined;

  const handleRegisterCtp = async () => {
    if (!merchantId) return;

    try {
      await registerCtp({ merchantId }).unwrap();
      toast.success('Click to Pay registration successful');
    } catch {
      toast.error(
        'An error occurred while registering Click to Pay. Please try again later or contact Skipify Support',
      );
    }
  };

  return (
    <PageWrapper
      data-testid="CLICK_TO_PAY"
      title="Click to Pay"
      description="Manage the Click to Pay configuration for this merchant."
      isLoading={isLoadingConfigs || merchantInfoLoading}
    >
      <Box sx={{ maxWidth: 600 }}>
        {ctpConfig ? (
          <Stack gap={4}>
            <Alert severity="success">Click to Pay is active for this merchant.</Alert>
            <Stack
              gap={2}
              sx={{
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.400',
              }}
            >
              <Typography component="h2" variant="large" fontWeight="semibold">
                Click to Pay Details
              </Typography>
              <Box>
                <Typography variant="small" fontWeight="semibold" color="text.secondary">
                  SRC DPA ID
                </Typography>
                <Typography variant="medium">{ctpConfig.value.srcDpaId}</Typography>
              </Box>
              <Box>
                <Typography variant="small" fontWeight="semibold" color="text.secondary">
                  DPA Name
                </Typography>
                <Typography variant="medium">{ctpConfig.value.dpaName}</Typography>
              </Box>
              <Box>
                <Typography variant="small" fontWeight="semibold" color="text.secondary">
                  Dynamic Data Type
                </Typography>
                <Typography variant="medium">{ctpConfig.value.dynamicDataType}</Typography>
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Box>
            <Alert severity="warning">Click to Pay is not yet configured for this merchant.</Alert>
            <Typography variant="medium" sx={{ mt: 4, mb: 2 }}>
              Click the button below to register this merchant with Click to Pay. This will enable
              streamlined checkout experiences for customers.
            </Typography>
            <Button
              text="Register Click to Pay"
              onClick={handleRegisterCtp}
              loading={isRegistering}
              disabled={false}
              theme="primary"
              sx={{ px: 4, width: 'fit-content' }}
            />
          </Box>
        )}
      </Box>
    </PageWrapper>
  );
};
